import { z } from 'zod';
import { ESTADOS_PROYECTO, MODALIDADES_MONTO, MODOS_FACTURACION, SALUD_PROYECTO, } from '../dominio/enums.js';
import { enumDe, fechaOpcional, fechaSchema, idSchema, montoSchema, paginacionSchema, textoOpcional, textoRequerido, } from './comunes.js';
// ─────────────────────────────────────────────────────────────
// Proyectos
// ─────────────────────────────────────────────────────────────
export const crearProyectoSchema = z.object({
    /** Licitación de origen. Opcional: también se registran proyectos fuera de Mercado Público. */
    licitacionId: idSchema.optional().nullable(),
    organismoId: idSchema.optional().nullable(),
    nombre: textoRequerido(300, 'El nombre del proyecto'),
    descripcion: textoOpcional(4000),
    estado: enumDe(ESTADOS_PROYECTO).default('PLANIFICADO'),
    /** Modalidad preferida para ingresar/visualizar montos del proyecto. */
    modalidadMonto: enumDe(MODALIDADES_MONTO).default('BRUTO'),
    /** Monto del contrato, **neto**. */
    montoContratoNeto: montoSchema,
    /** Monto del contrato, **bruto** (con IVA incluido). */
    montoContratoBruto: montoSchema.optional(),
    fechaInicio: fechaSchema,
    fechaFinPlanificada: fechaOpcional,
    fechaFinReal: fechaOpcional,
    responsableId: idSchema.optional().nullable(),
    /**
     * Presupuesto de costos planificado. Es la referencia contra la que se mide la
     * desviación; sin él solo se puede saber cuánto se gastó, no si fue mucho.
     */
    presupuestoCostoPlanificado: montoSchema.default(0),
    /** Presupuesto de costos planificado en bruto (con IVA). */
    presupuestoCostoPlanificadoBruto: montoSchema.optional(),
    modoFacturacion: enumDe(MODOS_FACTURACION).default('AL_CIERRE'),
    numeroOrdenCompra: textoOpcional(60),
    fechaOrdenCompra: fechaOpcional,
    notas: textoOpcional(4000),
});
export const actualizarProyectoSchema = crearProyectoSchema.partial();
/**
 * Esquema de edición segura del proyecto.
 *
 * REGLA DE NEGOCIO Y SEGURIDAD:
 * Solo permite editar nombre, descripción, responsable asignado, notas, modo de facturación
 * y número/fecha de Orden de Compra.
 *
 * PROHIBIDO ALTERAR EN EDICIÓN REGULAR:
 * - montos contractuales (montoContratoNeto, montoContratoBruto)
 * - titularidad / cliente (organismoId, licitacionId)
 * - plazos contractuales (fechaInicio, fechaFinPlanificada)
 */
export const editarProyectoSeguroSchema = z.object({
    nombre: textoRequerido(300, 'El nombre del proyecto').optional(),
    descripcion: textoOpcional(4000),
    responsableId: idSchema.optional().nullable(),
    modoFacturacion: enumDe(MODOS_FACTURACION).optional(),
    numeroOrdenCompra: textoOpcional(60),
    fechaOrdenCompra: fechaOpcional,
    notas: textoOpcional(4000),
});
export const cambiarEstadoProyectoSchema = z.object({
    estado: enumDe(ESTADOS_PROYECTO),
    motivo: textoOpcional(1000),
});
export const cerrarProyectoSchema = z.object({
    fechaFinReal: fechaOpcional,
    /** Si `false`, el cierre se rechaza cuando quedan ítems de checklist o saldos pendientes. */
    forzar: z.boolean().default(false),
    motivoForzado: textoOpcional(2000),
    notaCierre: textoOpcional(2000),
});
export const filtrosProyectoSchema = paginacionSchema.extend({
    busqueda: z.string().trim().max(200).optional(),
    estado: enumDe(ESTADOS_PROYECTO).optional(),
    salud: enumDe(SALUD_PROYECTO).optional(),
    responsableId: idSchema.optional(),
    organismoId: idSchema.optional(),
    fechaFinDesde: fechaOpcional,
    fechaFinHasta: fechaOpcional,
    /** `true` para traer solo los que están sobre presupuesto o por vencer. */
    soloEnRiesgo: z.coerce.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────
// Hitos
// ─────────────────────────────────────────────────────────────
export const crearHitoSchema = z.object({
    proyectoId: idSchema,
    nombre: textoRequerido(250, 'El nombre del hito'),
    descripcion: textoOpcional(2000),
    fechaPlanificada: fechaOpcional,
    fechaReal: fechaOpcional,
    completado: z.boolean().default(false),
    /** Marca el hito como generador de una cuota de facturación. */
    esFacturable: z.boolean().default(false),
    /** Monto neto a facturar en este hito. Si va vacío, se reparte el saldo del contrato. */
    montoFacturable: montoSchema.optional().nullable(),
    orden: z.number().int().min(0).default(0),
});
export const actualizarHitoSchema = crearHitoSchema.omit({ proyectoId: true }).partial();
export const reordenarHitosSchema = z.object({
    ordenes: z.array(z.object({ id: idSchema, orden: z.number().int().min(0) })).min(1),
});
// ─────────────────────────────────────────────────────────────
// Checklist de cierre y bitácora
// ─────────────────────────────────────────────────────────────
export const crearItemCierreSchema = z.object({
    proyectoId: idSchema,
    texto: textoRequerido(300, 'La descripción del ítem'),
    completado: z.boolean().default(false),
});
export const crearNotaSchema = z.object({
    proyectoId: idSchema,
    texto: textoRequerido(4000, 'El texto de la nota'),
});
// ─────────────────────────────────────────────────────────────
// Presupuesto de costos
// ─────────────────────────────────────────────────────────────
export const definirPresupuestoSchema = z.object({
    proyectoId: idSchema,
    lineas: z
        .array(z.object({
        categoriaGastoId: idSchema,
        montoPlanificado: montoSchema,
    }))
        .min(1, 'Define al menos una línea de presupuesto'),
});
/**
 * Regenera el cronograma de un proyecto.
 *
 * Solo toca cuotas todavía `PROYECTADA`: las que ya se convirtieron en factura
 * son hechos consumados y no se pueden reescribir desde acá.
 */
export const generarCronogramaSchema = z.object({
    proyectoId: idSchema,
    modo: enumDe(MODOS_FACTURACION).optional(),
    /** Duración en meses para contratos (ej. 12, 24, 48 meses). */
    duracionMeses: z.number().int().min(1).max(120).optional(),
    /** Sobrescribe la condición de pago del tenant solo para este cronograma. */
    condicionPago: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────
// Gestión Manual de Cuotas de Facturación
// ─────────────────────────────────────────────────────────────
export const crearCuotaManualSchema = z.object({
    proyectoId: idSchema,
    descripcion: textoRequerido(200, 'El concepto o descripción de la cuota'),
    fechaEmisionEstimada: fechaSchema,
    fechaCobroEstimada: fechaOpcional,
    /** Monto neto de la cuota. */
    montoNeto: montoSchema,
    /** Monto total en bruto opcional; si no viene se calcula con IVA 19%. */
    montoTotal: montoSchema.optional(),
    hitoId: idSchema.optional().nullable(),
});
export const actualizarCuotaManualSchema = z.object({
    descripcion: textoRequerido(200, 'El concepto o descripción de la cuota').optional(),
    fechaEmisionEstimada: fechaSchema.optional(),
    fechaCobroEstimada: fechaOpcional,
    montoNeto: montoSchema.optional(),
    montoTotal: montoSchema.optional(),
    hitoId: idSchema.optional().nullable(),
});
// ─────────────────────────────────────────────────────────────
// Acciones Rápidas de 1 Clic y 7 Pestañas de la Ficha de Proyecto
// ─────────────────────────────────────────────────────────────
/**
 * Registro de gasto express en 15 segundos directamente en la ficha del proyecto.
 */
export const crearGastoProyectoSchema = z.object({
    categoriaGastoId: idSchema,
    concepto: textoRequerido(250, 'El concepto o detalle del gasto'),
    proveedorNombre: textoOpcional(200),
    proveedorId: idSchema.optional().nullable(),
    montoNeto: montoSchema.optional(),
    montoTotal: montoSchema.optional(),
    ivaPct: z.number().int().min(0).max(100).optional(),
    tipoDocumento: enumDe([
        'FACTURA',
        'FACTURA_ELECTRONICA',
        'BOLETA',
        'BOLETA_ELECTRONICA',
        'BOLETA_HONORARIOS',
        'RECIBO',
        'SIN_DOCUMENTO',
    ]).default('FACTURA'),
    numeroDocumento: textoOpcional(60),
    estadoPago: enumDe(['PENDIENTE', 'PAGADO']).default('PENDIENTE'),
    fecha: fechaSchema.optional(),
    fechaPago: fechaOpcional,
    notas: textoOpcional(2000),
});
/**
 * Emisión o registro directo de factura de venta asociada al proyecto.
 */
export const emitirFacturaProyectoSchema = z.object({
    organismoId: idSchema.optional().nullable(),
    cuotaId: idSchema.optional().nullable(),
    hitoId: idSchema.optional().nullable(),
    numero: textoRequerido(60, 'El número o folio de la factura'),
    folioSii: textoOpcional(60),
    tipoDocumento: enumDe([
        'FACTURA_ELECTRONICA',
        'FACTURA_EXENTA',
        'BOLETA',
        'NOTA_CREDITO',
        'NOTA_DEBITO',
    ]).default('FACTURA_ELECTRONICA'),
    fechaEmision: fechaSchema.optional(),
    condicionPago: enumDe([
        'CONTADO',
        'DIAS_15',
        'DIAS_30',
        'DIAS_45',
        'DIAS_60',
        'DIAS_90',
    ]).optional(),
    montoNeto: montoSchema,
    ivaPct: z.number().int().min(0).max(100).optional(),
    numeroOrdenCompra: textoOpcional(60),
    notas: textoOpcional(2000),
});
/**
 * Registro de pago recibido en 1 clic desde la pestaña de facturación.
 */
export const registrarPagoProyectoSchema = z.object({
    facturaId: idSchema,
    monto: montoSchema.optional(),
    fecha: fechaSchema.optional(),
    medio: enumDe([
        'TRANSFERENCIA',
        'CHEQUE',
        'EFECTIVO',
        'COMPENSACION',
        'OTRO',
    ]).default('TRANSFERENCIA'),
    referencia: textoOpcional(100),
    notas: textoOpcional(2000),
});
//# sourceMappingURL=ejecucion.js.map